export function speak(text, voiceURI, voiceSpeed) {
  const speakWithVoice = () => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      const voices = speechSynthesis.getVoices()
      const selectedVoice = voices.find((voice) => voice.voiceURI === voiceURI)
      if (selectedVoice) {
        utterance.voice = selectedVoice
      }
      // Guard against an undefined/NaN speed: setting utterance.rate to a
      // non-number makes some browsers play back at a broken, very fast rate.
      utterance.rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
      speechSynthesis.speak(utterance)
      console.log('Text: ', text)
      console.log('Voice URI: ', voiceURI)
    } else {
      console.log('Web Speech API not supported')
    }
  }

  if (speechSynthesis.getVoices().length === 0) {
    speechSynthesis.addEventListener('voiceschanged', speakWithVoice)
  } else {
    speakWithVoice()
  }
}

// Stop whatever is currently being spoken.
export function stopSpeaking() {
  if ('speechSynthesis' in window) speechSynthesis.cancel()
}

// Keep a reference to the live utterance: Chrome garbage-collects utterances
// mid-speech otherwise, and their events silently stop firing.
let currentUtterance = null

// Speak with progress callbacks so a view can highlight the word being read.
//   onBoundary({ charIndex, charLength, estimated }) — per word where the
//   browser reports boundaries. Where it never does (Safari, many CJK voices)
//   a timer estimates the position from characters-per-second × rate, so the
//   highlight still moves along the text. `cps` tunes that estimate (Latin
//   script ≈ 13, Korean / Japanese / Chinese ≈ 4.5).
// Returns { cancel, promise } — the promise resolves when speech ends, which
// lets a "read all" chain sentences one after another.
export function speakTracked(text, voiceURI, voiceSpeed, { onStart, onBoundary, onEnd, cps } = {}) {
  let resolveDone
  const promise = new Promise((r) => { resolveDone = r })
  if (!('speechSynthesis' in window) || !text) {
    if (onEnd) onEnd()
    resolveDone()
    return { cancel() {}, promise }
  }
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
  let timer = null
  let gotBoundary = false
  let ended = false
  const finish = () => {
    if (ended) return
    ended = true
    if (timer) clearInterval(timer)
    if (onEnd) onEnd()
    resolveDone()
  }
  const start = () => {
    const utterance = new SpeechSynthesisUtterance(text)
    const v = speechSynthesis.getVoices().find((x) => x.voiceURI === voiceURI)
    if (v) utterance.voice = v
    utterance.rate = rate
    utterance.onstart = () => {
      if (onStart) onStart()
      const t0 = performance.now()
      const charsPerSec = (cps || 12) * rate
      timer = setInterval(() => {
        if (gotBoundary || ended) { clearInterval(timer); return }
        const idx = Math.min(text.length - 1, Math.floor(((performance.now() - t0) / 1000) * charsPerSec))
        if (onBoundary) onBoundary({ charIndex: idx, estimated: true })
      }, 90)
    }
    utterance.onboundary = (e) => {
      if (e.name && e.name !== 'word') return
      gotBoundary = true
      if (timer) clearInterval(timer)
      if (onBoundary) onBoundary({ charIndex: e.charIndex || 0, charLength: e.charLength, estimated: false })
    }
    utterance.onend = finish
    utterance.onerror = finish
    currentUtterance = utterance
    speechSynthesis.cancel() // one voice at a time
    speechSynthesis.speak(utterance)
  }
  if (speechSynthesis.getVoices().length === 0) speechSynthesis.addEventListener('voiceschanged', start, { once: true })
  else start()
  // Only cancel the engine if THIS utterance is still the live one — otherwise
  // an unmounting component would cut off whatever started speaking after it.
  return { cancel: () => { if (!ended) { speechSynthesis.cancel(); finish() } }, promise }
}
